import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { usePage } from '@inertiajs/react'
import type { Language } from '@/types/language'
import type { SharedProps } from '@/types'
import {
  fetchLanguages,
  createLanguage,
  updateLanguage,
  enrollInLanguage,
  unenrollFromLanguage,
} from '@/api/languages'

export interface LanguagesContextValue {
  languages: Language[]
  loading: boolean
  selectedLanguageId: number | null
  setSelectedLanguageId: (id: number | null) => void
  addLanguage: (data: { iso_code: string; name: string; description: string }) => Promise<Language>
  editLanguage: (id: number, data: { iso_code: string; name: string; description: string }) => Promise<void>
  enroll: (id: number) => Promise<void>
  unenroll: (id: number) => Promise<void>
}

const LanguagesContext = createContext<LanguagesContextValue | null>(null)

export function LanguagesProvider({ children }: { children: ReactNode }) {
  const props = usePage<SharedProps>().props
  const userId = props.auth?.user?.id ?? null

  const [languages, setLanguages] = useState<Language[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedLanguageId, setSelectedLanguageIdState] = useState<number | null>(() => {
    if (typeof window === 'undefined') return null
    const stored = localStorage.getItem('selectedLanguageId')
    return stored ? Number(stored) : null
  })

  useEffect(() => {
    if (userId === null) {
      setLanguages([])
      setLoading(false)
      return
    }
    setLoading(true)
    fetchLanguages()
      .then((res) => setLanguages(res.data.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [userId])

  const setSelectedLanguageId = useCallback((id: number | null) => {
    setSelectedLanguageIdState(id)
    if (id !== null) {
      localStorage.setItem('selectedLanguageId', String(id))
    } else {
      localStorage.removeItem('selectedLanguageId')
    }
  }, [])

  const addLanguage = useCallback(async (data: { iso_code: string; name: string; description: string }) => {
    const res = await createLanguage(data)
    const lang: Language = { ...res.data.data, enrolled: false }
    setLanguages((prev) => [...prev, lang].sort((a, b) => a.name.localeCompare(b.name)))
    return lang
  }, [])

  const editLanguage = useCallback(async (id: number, data: { iso_code: string; name: string; description: string }) => {
    const res = await updateLanguage(id, data)
    setLanguages((prev) =>
      prev.map((l) => (l.id === id ? { ...l, ...res.data.data } : l))
        .sort((a, b) => a.name.localeCompare(b.name)),
    )
  }, [])

  const enroll = useCallback(async (id: number) => {
    setLanguages((prev) =>
      prev.map((l) => (l.id === id ? { ...l, enrolled: true } : l)),
    )
    try {
      await enrollInLanguage(id)
    } catch {
      setLanguages((prev) =>
        prev.map((l) => (l.id === id ? { ...l, enrolled: false } : l)),
      )
    }
  }, [])

  const unenroll = useCallback(async (id: number) => {
    setLanguages((prev) =>
      prev.map((l) => (l.id === id ? { ...l, enrolled: false } : l)),
    )
    try {
      await unenrollFromLanguage(id)
    } catch {
      setLanguages((prev) =>
        prev.map((l) => (l.id === id ? { ...l, enrolled: true } : l)),
      )
    }
  }, [])

  return (
    <LanguagesContext.Provider value={{
      languages,
      loading,
      selectedLanguageId,
      setSelectedLanguageId,
      addLanguage,
      editLanguage,
      enroll,
      unenroll,
    }}>
      {children}
    </LanguagesContext.Provider>
  )
}

export function useLanguagesContext(): LanguagesContextValue {
  const ctx = useContext(LanguagesContext)
  if (!ctx) throw new Error('useLanguagesContext must be used within LanguagesProvider')
  return ctx
}
